import { ApiFailure } from "@/components/data-display/api-failure";
import { EmptyState } from "@/components/ui/feedback";
import { LoadingState, LoadingTable, PermissionDeniedState } from "@/components/ui/system-state";
import { useMemo } from "react";
import { CheckCircle2, RefreshCw } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatCardRow } from "@/components/data-display/stat-card";
import { ApprovalCard, ApprovedRow } from "@/features/approvals/approval-card";
import { useApprovals } from "@/hooks/use-approvals";
import { usePermission } from "@/hooks/use-permission";
import { Link } from "react-router-dom";
import { Button, buttonVariants } from "@/components/ui/button";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { classifyFailure, failureFromReason } from "@/lib/api-failure";
import type { ApiErrorInfo } from "@/lib/api";

// APR-01/APR-02 (defect D04): the two "All caught up" empties left the viewer
// with nothing to do. Both carry this link to the transfer form; navigation
// permission is handled by the destination page, not here.
const createTransferAction = (
  <Link to="/transfers/new" className={buttonVariants({ size: "sm" })}>
    Create a transfer
  </Link>
);

/** The role that grants the approvals queue, by its role-catalog name. */
const APPROVER_ROLE_LABEL = "Governed transfer approver";

/**
 * APR-01 (UX audit D04): a denied approvals read used to fall through the shared
 * ApiFailure card — "You don't have access to your approvals" — which named the
 * symptom but not the role that fixes it, and left an administrator with no way
 * through. The sentence now names the role that grants the queue, and a person
 * who may assign roles gets a direct link to the people who hold them. Who is
 * denied is unchanged: this renders only for the same failures that already
 * classified as denied.
 */
function ApprovalsAccessDenied({ error, onRetry }: { error: ApiErrorInfo; onRetry: () => void }) {
  // The link is an administrator's shortcut, not a second access rule: the
  // permission to assign roles (`role:full`) decides whether it shows, and the
  // destination route keeps its own guard.
  const canManageRoles = usePermission("role:full");
  return (
    <div>
      <PermissionDeniedState
        title="Access required"
        description={`Approving transfers needs the ${APPROVER_ROLE_LABEL} role. Ask an organization admin to assign it.`}
        details={error.capability ? [{ k: "Needs", v: error.capability }] : undefined}
        action={
          <>
            {canManageRoles && (
              <Link to="/admin/users" className={buttonVariants({ size: "sm" })}>
                Manage roles
              </Link>
            )}
            <Button variant="secondary" onClick={onRetry}>Try again</Button>
          </>
        }
      />
      <SupportDetails reference={error.supportReference} className="mt-3" />
    </div>
  );
}

export default function ApprovalsPage() {
  const { mine, allPending, completed, escalated, loading, error, refresh } = useApprovals();

  // The three KPI tiles are computed only from the live lists the hook returns
  // — one tile per queue the gateway actually serves. "Avg Response Time" and
  // "SLA Breaches (7d)" had no backend field behind them
  // (GatewayApprovalResponse carries no SLA/response-time data) and were
  // hardcoded placeholders — removed rather than shown as fake numbers
  // (ALL-4734 G6 mock/fabrication audit). The row renders only after a
  // successful read: a denial or a failed read returns early below, so a
  // refused queue can never be shown as a confident 0.
  const stats = useMemo(() => [
    { value: String(mine.length), label: "Assigned to Me" },
    { value: String(allPending.length), label: "All Pending" },
    { value: String(completed.length), label: "Completed" },
  ], [mine, allPending, completed]);

  if (loading) {
    return (
      <div className="space-y-3">
        <LoadingState rows={2} />
        <LoadingTable rows={4} cols={4} label="Loading approvals" />
      </div>
    );
  }

  if (error) {
    const failure = failureFromReason(error);
    if (classifyFailure(failure) === "denied") {
      return <ApprovalsAccessDenied error={failure} onRetry={refresh} />;
    }
    return <ApiFailure error={error} subject="your approvals" onRetry={refresh} />;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs font-medium text-muted hover:text-ink"
          aria-label="Refresh approvals"
        >
          <RefreshCw className="size-3.5" aria-hidden />
        </button>
      </div>

      {/* Headline queue counts, computed from the live lists. The tabs filter
          the same queues and carry their own counts; the tiles are the
          at-a-glance summary shown on every tab. */}
      <div data-testid="approvals-kpi">
        <StatCardRow stats={stats} className="lg:grid-cols-3" />
      </div>

      <Tabs defaultValue="assigned">
        <TabsList>
          <TabsTrigger value="assigned" count={mine.length}>Assigned to Me</TabsTrigger>
          <TabsTrigger value="all-pending" count={allPending.length}>All Pending</TabsTrigger>
          <TabsTrigger value="completed" count={completed.length} countMuted>Completed</TabsTrigger>
          <TabsTrigger value="escalated" count={escalated.length} countMuted>Escalated</TabsTrigger>
        </TabsList>

        <TabsContent value="assigned" forceMount>
          <div className="flex flex-col gap-3">
            {mine.map((a) => (
              <ApprovalCard key={a.id} approval={a} />
            ))}
            {mine.length === 0 && (
              <EmptyState
                tone="positive"
                icon={<CheckCircle2 aria-hidden className="size-6 text-success" />}
                title="All caught up"
                description="No approvals are waiting on you. A transfer needs approval when its policy asks for one; approvers are named by your organization administrator."
                action={createTransferAction}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="all-pending" forceMount>
          <div className="flex flex-col gap-3">
            {allPending.map((a) => (
              <ApprovalCard key={a.id} approval={a} />
            ))}
            {allPending.length === 0 && (
              <EmptyState
                tone="positive"
                icon={<CheckCircle2 aria-hidden className="size-6 text-success" />}
                title="All caught up"
                description="No approvals are pending in this organisation."
                action={createTransferAction}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="completed" forceMount>
          <div className="rounded-lg border border-line bg-surface">
            {completed.map((a) => (
              <ApprovedRow key={a.id} approval={a} />
            ))}
            {completed.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-muted">
                No completed approvals yet.
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="escalated" forceMount>
          <div className="flex flex-col gap-3">
            {escalated.map((a) => (
              <ApprovalCard key={a.id} approval={a} />
            ))}
            {escalated.length === 0 && (
              // Design-port task 2: the Escalated tab used the prototype's
              // bespoke dashed <div>; it now uses the shared EmptyState like
              // its sibling tabs. Kept on the neutral tone (not the "All
              // caught up" positive one) because an empty escalated queue is
              // not an assignment the operator has cleared, and the copy is
              // the prototype's own sentence, unchanged.
              <EmptyState title="No escalated approvals" />
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
